import { Modal } from "../../../components/modals/Modal";
import { GeneralVideoPlayer } from "../../../components/others/GeneralVideoPlayer";
import { ProgressBar } from "../../../components/progressbar/ProgressBar";
import { StackedProgressBar } from "../../../components/progressbar/StackedProgressBar";
import { graphColors } from "../../../services/entity/chart/chart-service";
import React, { useState, useEffect } from "react";
import { CONSTRUCTION_EVENT_TYPES } from "../../../services/gallery/gallery-service";
import { getInfluxdbData } from "../../../services/api/influxdb-tools";
import BeepOn from "../../../assets/images/beep_on.png";
import BeepOff from "../../../assets/images/beep_off.png";
import { formatSecondsToFullDateTime } from "../../../helpers/date/date-helper";
import { HeartIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { patchData } from "../../../services/api/api-tools";
import { useAlert } from "../../../contexts/AlertContext";

export const VideoDetailsModal = ({ selectedRecord, src, isOpen, setIsOpen, refetchRecords }) => {

  const { showAlert } = useAlert();

  const [elapsedTime, setElapsedTime] = useState(0);
  const [data, setData] = useState([]);

  const [recordComments, setRecordComments] = useState('');

  useEffect( () => {
    if (!selectedRecord) return;

    setRecordComments(selectedRecord.comments);

    const query = `
      import "strings" from(bucket: "engine")
      |> range(start: ${selectedRecord.date}, stop: ${selectedRecord.date + selectedRecord.duration})
      |> filter(fn: (r) => r["_measurement"] == "event_scores")
      |> filter(fn: (r) => r["client_id"] == "${selectedRecord.client_id}")
      |> filter(fn: (r) => r["event_type"] == "${selectedRecord.data?.event_name}")
      |> aggregateWindow(every: 500ms, fn: max, createEmpty: true)
      |> pivot(rowKey: ["_time"], columnKey: ["_field"], valueColumn: "_value")
    `;

    const getData = async () => {
      const newData = await getInfluxdbData(
        'engine',
        'event_scores',
        [],
        [],
        null,
        null,
        null,
        null,
        null,
        [],
        null,
        query,
        false,
        null,
      );
      setData(newData);
    };

    getData();
  }, [selectedRecord]);

  const valueAtTime = (series, t, step = 0.5) => {
    if (!series) return null;

    if (t >= 0 && t / step < series.length)
      return series[Math.floor(t / step)] ?? null;
    else
      return null;
  };

  const handleProgress = (progress) => {
    setElapsedTime(progress.playedSeconds);
  };

  const handleCommentsBlur = async () => {
    try {
      let path = `records/${selectedRecord.id}`;
      let body = { comments: recordComments || null };
      const response = await patchData(path, body);
      if (response) {
        showAlert('success', 'Les commentaires ont été modifiés.');
        refetchRecords();
      } else {
        showAlert('warning', 'Oops! We encountered an issue.');
      }
    } catch (err) {
      console.error('Something bad happened:', err);
    }
  };

  const handleFavoriteClick = async () => {
    try {
      let path = `records/${selectedRecord.id}`;
      let body = { is_favorite: !selectedRecord.is_favorite };
      const response = await patchData(path, body);
      if (response) {
        selectedRecord.is_favorite = !selectedRecord.is_favorite;
        refetchRecords();
      } else {
        showAlert('warning', 'Oops! We encountered an issue.');
      }
    } catch (err) {
      console.error('Something bad happened:', err);
    }
  };

  const eventType = selectedRecord?.data ?
    CONSTRUCTION_EVENT_TYPES.find((eventType) => eventType.name === selectedRecord.data?.event_name) : null;

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen} size="full">
      <div className='flex gap-4 relative h-[80vh]'>
        {
          selectedRecord && eventType &&
          <>
            <div className="w-9/12 h-full">
              <GeneralVideoPlayer videoUrl={src} handleOnProgress={handleProgress} startPlaying={false} startProgress={selectedRecord.data?.max_time} />
            </div>
            <div className="flex flex-col gap-4 pt-10 overflow-hidden w-3/12 h-full relative">
              <div className="absolute top-0 left-0 flex mt-1">
                <button className="rounded-full inline-block" onClick={handleFavoriteClick}>
                  <HeartIcon className={`w-6 h-6 ${selectedRecord.is_favorite ? 'text-perception-blue' : 'text-perception-gray-100'} hover:text-perception-blue`}/>
                </button>
              </div>

              {/* Top icons and info */}
              <div className="absolute top-0 right-0 flex items-center">
                <span className="rounded-md backdrop-blur-md bg-perception-gray-100 px-2 py-1 text-xs font-medium inline-block ml-4">
                  #{selectedRecord.id}
                </span>
                <span className="rounded-md backdrop-blur-md bg-perception-gray-100 px-2 py-1 text-xs font-medium inline-block ml-4">
                  {formatSecondsToFullDateTime(selectedRecord.date)}
                </span>
                {/*<span className="rounded-md backdrop-blur-md bg-perception-gray-100 px-2 py-1 text-xs font-medium inline-block ml-4">*/}
                {/*  {CONSTRUCTION_EVENT_TYPES.find((event_type) => event_type.name === selectedRecord.data?.event_name)?.tag_text || 'AUTRES'}*/}
                {/*</span>*/}
                <button className="rounded-full backdrop-blur-md bg-perception-gray-100 px-2 py-2 text-xs font-medium inline-block ml-4" onClick={() => setIsOpen(false)}>
                  <XMarkIcon className="w-4 h-4"/>
                </button>
              </div>

              {/* Record scores */}
              <div key={0} className="bg-perception-gray-100 rounded-md p-4">
                <h3 className="font-bold">{eventType.display_text}</h3>
                <StackedProgressBar
                  values={eventType.criteria.map((criterion) => valueAtTime(data, elapsedTime)?.[criterion.name] * 100 || 0)}
                  weights={eventType.criteria.map((criterion) => criterion.weight)}
                  maxValue={valueAtTime(data, elapsedTime)?.final_score * 100}
                  threshold={eventType.threshold}
                  bgColor="white"
                  className="my-4"
                />
                {
                  eventType.criteria.map((criterion, criterionIndex) => (
                    <React.Fragment key={criterionIndex}>
                      <h4 className="text-xs mt-2">{criterion.display_text}</h4>
                      <ProgressBar
                        value={valueAtTime(data, elapsedTime)?.[criterion.name] * 100 || 0}
                        color={graphColors[criterionIndex]}
                        bgColor="white"
                        size="small"
                        className="mt-1"
                      />
                    </React.Fragment>
                  ))
                }
              </div>
              <div>
                <textarea
                  name="comments"
                  className="w-full bg-perception-gray-100 rounded-md p-4 focus:outline-none resize-none"
                  placeholder="Ajouter des commentaires"
                  value={recordComments}
                  onChange={(e) => setRecordComments(e.target.value)}
                  onBlur={handleCommentsBlur}
                />
              </div>
              <div className="flex-grow relative">
                <img
                  src={valueAtTime(data, elapsedTime)?.final_score * 100 > eventType?.threshold ? BeepOn : BeepOff}
                  alt="beep"
                  className="absolute inset-0 h-full w-full object-contain"
                />
              </div>
            </div>
          </>
        }
      </div>
    </Modal>
  );
};
